import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MsalProvider } from '@azure/msal-react';
import { broadcastResponseToMainFrame } from '@azure/msal-browser/redirect-bridge';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import App from './App';
import { AUTH_CONFIG_ERROR, MAJANS_TENANT_ID, msalInstance } from './config/auth';
import { isMsalChildWindow, returnPathFrom } from './config/authSignals';
import { savedTheme, showTheme } from './lib/theme';

// Applied BEFORE the first render, so nobody sees a flash of the wrong theme.
// No saved choice means "follow the device" — lib/theme.ts says why that is
// never written down as a default.
showTheme(savedTheme());

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('#root is missing from index.html');
const root = createRoot(rootElement);

/**
 * MSAL must be initialised and the redirect response consumed BEFORE React
 * renders, or the first render happens with no accounts and the app decides the
 * user is signed out at the exact moment they are coming back from signing in.
 *
 * Wrapped in a function rather than written as a top-level `await`: a top-level
 * await in the entry chunk depends on the build target, and when it is not
 * supported the failure is a blank page with a syntax error, not a warning.
 */
async function bootstrap(): Promise<void> {
  // MSAL's hidden iframe lands HERE, because the redirect URI is this origin. In
  // msal-browser v5 the window that asked is waiting on a BroadcastChannel for
  // this page to pass the answer back — and it waits in vain if the app renders
  // instead. Worse, the rendered app asks for a token and opens the next hidden
  // iframe: on 2026-10-02 that nested ~20 deep until Entra blocked the client as
  // a loop (AADSTS50196). So: hand the answer back, render nothing, and stop
  // BEFORE MSAL initialises, which would otherwise read the response itself.
  // config/authSignals.ts has the full account.
  if (isMsalChildWindow(window)) {
    try {
      await broadcastResponseToMainFrame();
    } catch (err) {
      // Nobody can see this page. The asking window times out and redirects to
      // sign in (acquireApiToken), which is the recovery either way.
      console.error('[auth] could not hand the sign-in response back to the app', err);
    }
    return;
  }

  // Skipped when the build has no client id: MSAL would be initialising against
  // a placeholder, and its failure would be a message about the application
  // registration — which sends whoever reads it into Entra looking for a problem
  // that is in the deploy. App.tsx renders the real reason instead.
  if (!AUTH_CONFIG_ERROR) {
    await msalInstance.initialize();

    const redirectResult = await msalInstance.handleRedirectPromise();
    if (redirectResult?.account) {
      msalInstance.setActiveAccount(redirectResult.account);
      // Back to the page that was asked for (App.tsx sends it as `state`), before
      // the router reads the URL. Same origin only - returnPathFrom refuses the rest.
      const back = returnPathFrom(redirectResult.state);
      if (back) window.history.replaceState(null, '', back);
    } else if (!msalInstance.getActiveAccount()) {
      // A returning user with a cached account and no redirect in flight. Only a
      // Majans-tenant account is eligible — no other tenant can be issued a token
      // for this API, so picking one would guarantee a silent renewal failure.
      const known = msalInstance
        .getAllAccounts()
        .find((account) => account.tenantId === MAJANS_TENANT_ID);
      if (known) msalInstance.setActiveAccount(known);
    }
  }

  root.render(
    <StrictMode>
      <MsalProvider instance={msalInstance}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </MsalProvider>
    </StrictMode>,
  );
}

void bootstrap().catch((err: unknown) => {
  // Anything that goes wrong before the first render is a white screen otherwise:
  // React is not mounted, so no error boundary exists to catch it. Known
  // misconfiguration is reported through AUTH_CONFIG_ERROR / API_CONFIG_ERROR
  // instead and never reaches here; what lands in this branch is MSAL failing to
  // initialise — and "the screen is blank" is not a report anyone can act on.
  console.error('[boot] the app failed to start', err);
  root.render(
    <div className="flex h-full items-center justify-center p-s4">
      <div className="max-w-md rounded border border-hairline bg-card p-s4 shadow-card">
        <p className="text-base font-semibold text-ink">Quality could not start.</p>
        <p className="mt-s2 text-body text-ink-muted">
          {err instanceof Error ? err.message : String(err)}
        </p>
        <p className="mt-s3 text-body text-ink-muted">
          Sign-in could not be prepared. Reloading is worth one try; if it repeats, this is a
          deployment problem rather than yours.
        </p>
      </div>
    </div>,
  );
});

/* ── The offline shell ────────────────────────────────────
 * Registered AFTER the app mounts, never before: a service worker racing MSAL's
 * redirect handling is a class of bug that shows up only on a cold start on a
 * real device, which is the worst place to find it.
 *
 * ⚠ The worker never caches /api. See public/sw.js — a cached quality answer is
 * a confidently wrong one, and on this app that is worse than no answer.
 *
 * Registration failing is not fatal and must not look like it. The app works
 * exactly as it did before; it simply cannot start without a network.
 */
function registerOfflineShell(): void {
  if (!('serviceWorker' in navigator)) return;
  // Vite's dev server serves modules the worker has no business caching, and a
  // stale shell during development is hours of confusion for no benefit.
  if (import.meta.env.DEV) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err: unknown) => {
      console.warn('[offline] the app will still work, but cannot start without a network', err);
    });
  });
}

registerOfflineShell();
